import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Code, Copy, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { user } from '../api';

const CodeBlock = ({ title, code, language = 'html' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-xl overflow-hidden border border-white/10">
      <div className="flex items-center justify-between px-4 py-2 bg-white/5 border-b border-white/10">
        <span className="text-sm font-medium text-white/70">{title}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-2 py-1 rounded text-xs text-white/50 hover:text-white hover:bg-white/5 transition-colors"
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5 text-emerald-400" />
              Copied
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" />
              Copy
            </>
          )}
        </button>
      </div>
      <pre className="p-4 bg-[#0a0a0f] overflow-x-auto">
        <code className="text-sm text-blue-300 font-mono whitespace-pre">{code}</code>
      </pre>
    </div>
  );
};

const AccordionItem = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-white/10 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-[#111118] hover:bg-[#151520] transition-colors"
      >
        <span className="font-medium text-white">{title}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-white/40" />
        ) : (
          <ChevronDown className="w-5 h-5 text-white/40" />
        )}
      </button>
      {isOpen && <div className="p-4 bg-[#0d0d12] border-t border-white/5">{children}</div>}
    </div>
  );
};

const ImplementationPage = () => {
  const [apiKey, setApiKey] = useState('YOUR_API_KEY');

  useEffect(() => {
    const loadApiKey = async () => {
      try {
        const data = await user.getApiKey();
        if (data.apiKey) {
          setApiKey(data.apiKey);
        }
      } catch (error) {
        console.error('Failed to load API key:', error);
      }
    };
    loadApiKey();
  }, []);

  const shopifyCode = `<!-- Add to theme.liquid before </head> -->
<script src="https://api.vltryon.com/widget.js" 
        data-api-key="${apiKey}"></script>

<!-- Add to product template: button only if product exists, otherwise nothing -->
<span data-visionlive-try-on="{{ product.sku }}"></span>`;

  const woocommerceCode = `<!-- Add to header.php before </head> -->
<script src="https://api.vltryon.com/widget.js" 
        data-api-key="${apiKey}"></script>

<!-- Add to single-product.php: button only if product exists, otherwise nothing -->
<span data-visionlive-try-on="<?php echo esc_attr($product->get_sku()); ?>"></span>`;

  const customCode = `<!-- Add before </head> -->
<script src="https://api.vltryon.com/widget.js" 
        data-api-key="${apiKey}"></script>

<!-- Recommended: button only if product exists; otherwise nothing -->
<span data-visionlive-try-on="PRODUCT_SKU"></span>

<!-- Or use hasProduct() for custom UI: button only if exists, else nothing -->
<div id="try-on-placeholder"></div>
<script>
  VisionLive.on('ready', async () => {
    if (await VisionLive.hasProduct('PRODUCT_SKU')) {
      var btn = document.createElement('button');
      btn.textContent = 'Try On';
      btn.onclick = () => VisionLive.open('PRODUCT_SKU');
      document.getElementById('try-on-placeholder').appendChild(btn);
    }
  });
</script>`;

  const apiExample = `// Initialize widget with API call
const response = await fetch('https://api.vltryon.com/api/vto/init', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ${apiKey}',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({ sku: 'YOUR_PRODUCT_SKU' })
});

const config = await response.json();
// Response:
// {
//   product: {
//     sku: "RB-3025",
//     name: "Classic Aviator",
//     assetType: "2d",
//     frontImageUrl: "https://...",
//     handleImageUrl: "https://...",
//     templeConfig: { ... }
//   },
//   features: { recording: true, compare: true, sharing: true }
// }`;

  const fullCatalogPageCode = `<!-- Dedicated merchant VTO page (auto-load full catalog as fullscreen overlay) -->
<script src="https://api.vltryon.com/widget.js"
        data-api-key="${apiKey}"
        data-auto-open-catalog="true"></script>`;

  const fullCatalogInlineCode = `<!-- Optional inline embed mode (not overlay) -->
<script src="https://api.vltryon.com/widget.js"
        data-api-key="${apiKey}"></script>

<!-- data-vl-mode="inline" forces inline mount into this container -->
<div data-visionlive-catalog data-vl-mode="inline" data-vl-height="fullscreen"></div>`;

  const stockEndpointCode = `<!-- Optional: hide out-of-stock SKUs in VTO catalog -->
<script src="https://api.vltryon.com/widget.js"
        data-api-key="${apiKey}"
        data-stock-endpoint="https://partner-shop.com/api/vto-stock"
        data-stock-timeout-ms="3500"></script>

<!-- Supported JSON formats for stock endpoint -->
<!-- 1) { "items": [{ "sku": "RB-3025", "inStock": true }, { "sku": "RB-2140", "inStock": false }] } -->
<!-- 2) [{ "sku": "RB-3025", "inStock": true }, { "sku": "RB-2140", "inStock": false }] -->
<!-- 3) { "RB-3025": true, "RB-2140": false } -->`;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-white mb-2">Implementation Guide</h1>
        <p className="text-white/60">
          Add the VisionLive try-on widget to your e-commerce store in minutes.
        </p>
      </motion.div>

      {/* Quick Start */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-gradient-to-br from-blue-900/30 to-cyan-900/30 rounded-2xl p-6 border border-blue-500/20 mb-8"
      >
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Code className="w-5 h-5 text-blue-400" />
          Quick Start
        </h2>
        <p className="text-white/70 mb-4">
          Add the script and a placeholder. The Try On button appears only when a product exists for that SKU; otherwise nothing is shown.
        </p>
        <CodeBlock
          title="Basic Integration"
          code={`<script src="https://api.vltryon.com/widget.js" data-api-key="${apiKey}"></script>
<span data-visionlive-try-on="PRODUCT_SKU"></span>`}
        />
        <div className="mt-4" />
        <p className="text-white/70 mb-4">
          For a dedicated VTO page with full catalog and automatic loading on page open, use the catalog placeholder below.
        </p>
        <CodeBlock
          title="Dedicated Full Catalog Page"
          code={fullCatalogPageCode}
        />
        <div className="mt-4" />
        <CodeBlock
          title="Dedicated Full Catalog Page (Inline Optional)"
          code={fullCatalogInlineCode}
        />
        <div className="mt-4" />
        <p className="text-white/70 mb-4">
          If your stock is managed only on the partner shop, provide a stock endpoint. In catalog mode, only SKUs present in that JSON and marked in stock are shown.
        </p>
        <CodeBlock
          title="Optional Stock Endpoint Filter"
          code={stockEndpointCode}
        />
      </motion.div>

      {/* Platform-specific guides */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="space-y-4 mb-8"
      >
        <h2 className="text-xl font-bold text-white mb-4">Platform Guides</h2>

        <AccordionItem title="Shopify" defaultOpen>
          <div className="space-y-4">
            <p className="text-white/70">
              Add the widget to your Shopify store by editing your theme files.
            </p>
            <ol className="list-decimal list-inside space-y-2 text-white/60 text-sm">
              <li>Go to Online Store → Themes → Edit code</li>
              <li>Open <code className="text-blue-400">theme.liquid</code></li>
              <li>Add the script tag before <code className="text-blue-400">&lt;/head&gt;</code></li>
              <li>Edit your product template to add the try-on button</li>
            </ol>
            <CodeBlock title="Shopify Code" code={shopifyCode} />
          </div>
        </AccordionItem>

        <AccordionItem title="WooCommerce">
          <div className="space-y-4">
            <p className="text-white/70">
              Integrate with WordPress/WooCommerce by editing your theme or using a plugin.
            </p>
            <ol className="list-decimal list-inside space-y-2 text-white/60 text-sm">
              <li>Edit your theme's <code className="text-blue-400">header.php</code></li>
              <li>Add the script before <code className="text-blue-400">&lt;/head&gt;</code></li>
              <li>Edit <code className="text-blue-400">single-product.php</code> to add the button</li>
            </ol>
            <CodeBlock title="WooCommerce Code" code={woocommerceCode} />
          </div>
        </AccordionItem>

        <AccordionItem title="Custom / Other Platforms">
          <div className="space-y-4">
            <p className="text-white/70">
              For custom websites or other platforms, use the JavaScript API.
            </p>
            <CodeBlock title="Custom Integration" code={customCode} />
          </div>
        </AccordionItem>
      </motion.div>

      {/* API Reference */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mb-8"
      >
        <h2 className="text-xl font-bold text-white mb-4">API Reference</h2>
        <p className="text-white/60 mb-4">
          For advanced integrations, you can use our REST API directly.
        </p>
        <CodeBlock title="API Example (JavaScript)" code={apiExample} language="javascript" />
      </motion.div>

      {/* Important Notes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-amber-900/20 rounded-xl p-6 border border-amber-500/20"
      >
        <h3 className="font-bold text-amber-300 mb-3">Important Notes</h3>
        <ul className="space-y-2 text-sm text-amber-300/80">
          <li className="flex items-start gap-2">
            <span className="text-amber-400 mt-1">•</span>
            Make sure your domain is set in the allowed domain settings
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-400 mt-1">•</span>
            Use <code className="text-amber-200">data-visionlive-try-on=&quot;SKU&quot;</code> or <code className="text-amber-200">VisionLive.hasProduct(sku)</code>: if no model exists, no button is shown (display nothing)
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-400 mt-1">•</span>
            The SKU must match an active product in your dashboard
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-400 mt-1">•</span>
            With <code className="text-amber-200">data-stock-endpoint</code>, catalog works as a whitelist: SKU not in JSON list is hidden
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-400 mt-1">•</span>
            Localhost is automatically allowed for development testing
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-400 mt-1">•</span>
            The widget requires HTTPS in production (HTTP only works on localhost)
          </li>
        </ul>
      </motion.div>
    </div>
  );
};

export default ImplementationPage;

